// An organization's subscription (PLAN-offers.md §7, §8, §15): where the period stands, staff moves (any version and
// tier now, extend the period, grant credits), its credits and its orders (mark paid, cancel, draft an invoice).
// The subscription's amounts (prices, allowance, pool, credits) are in its currency, and credit grants in theirs, the
// organization's: balances are never converted (sculpt-api PLAN-multi-currency.md §2c).
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { OrderAmounts } from '../../components/vat';
import { OrderPaymentBadge, OrderPayments, PaymentsToggle } from '../../components/payment';
import { OrderRefunds, RefundsToggle } from '../../components/refund';
import { useAsync } from '../../hooks/useAsync';
import { dateOnly, dateTime, keyIn, money, toDecimal } from '../../services/format';
import { Alert, Badge, Button, Card, Cell, Confirm, Field, Input, KeyValue, Row, Select, Table, statusTone } from '../../components/ui';
import { subscriptionTone } from './OrgsList';
import type { OfferAdmin, OfferVersion, OrganizationDetail, Subscription } from '../../types/api';
import type { Run } from './OrgDetail';

export function SubscriptionTab({ org, busy, run }: { org: OrganizationDetail; busy: string | null; run: Run }) {
  const { t } = useTranslation();
  const billing = org.billing;
  const sub = billing?.subscription ?? null;
  return (
    <div className="flex flex-col gap-4">
      {billing?.reason && <Alert tone="warn">{t(`billing.reason.${billing.reason}`)}</Alert>}
      <div className="grid gap-4 lg:grid-cols-2">
        {sub ? <Current sub={sub} /> : <Card title={t('billing.subscription')}>{t('billing.none')}</Card>}
        <div className="flex flex-col gap-4">
          <SetSubscription org={org} sub={sub} busy={busy} run={run} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Extend org={org} disabled={!sub} busy={busy} run={run} />
            <Grant org={org} busy={busy} run={run} />
          </div>
        </div>
      </div>
      <Credits org={org} />
      <Orders org={org} busy={busy} run={run} />
    </div>
  );
}

/** "€100 (€110 of credits) + €50 × 2 = €200 / month excl. VAT", the tier and seats parts only when they apply. */
function priceText(sub: Subscription, t: (k: string, o?: Record<string, unknown>) => string, l: string): string {
  if (sub.trial || Number(sub.monthly_total) === 0) return t('billing.free');
  const parts: string[] = [];
  if (sub.credit_tier_price !== null) parts.push(t('billing.tierPart', { price: money(sub.credit_tier_price, sub.currency, l), usage: money(sub.credit_tier_usage, sub.currency, l) }));
  if (Number(sub.price_per_seat) > 0 || sub.credit_tier_price === null) parts.push(`${money(sub.price_per_seat, sub.currency, l)} × ${sub.seats}`);
  return `${parts.join(' + ')} = ${money(sub.monthly_total, sub.currency, l)} ${t('billing.perMonthHt')}`;
}

function Current({ sub }: { sub: Subscription }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const pool = sub.pool === null ? null : Number(sub.pool);
  const ratio = pool ? Math.min(1, Number(sub.pool_spent) / pool) : 0;
  return (
    <Card
      title={
        <>
          {sub.offer_name} <span className="font-mono text-[11px] text-faint">v{sub.version}</span> <Badge tone={subscriptionTone(sub.status)}>{t(`billing.status.${sub.status}`)}</Badge>
        </>
      }
      actions={sub.newer_version ? <Badge tone="warn">{t('billing.grandfathered')}</Badge> : undefined}
    >
      <KeyValue
        rows={[
          [t('billing.price'), priceText(sub, t, l)],
          [t('billing.seats'), t('billing.seatsUsed', { used: sub.seats_used, seats: sub.seats })],
          [t('billing.period.label'), `${dateOnly(sub.current_period_start, l)} → ${dateOnly(sub.current_period_end, l)}`],
          [t('billing.allowancePerSeat'), sub.allowance_per_seat_eur === null ? t('orgs.unlimited') : money(sub.allowance_per_seat_eur, sub.currency, l)],
          [t('billing.userCap'), sub.user_cap_eur === null ? '—' : money(sub.user_cap_eur, sub.currency, l)],
          [t('billing.credits'), money(sub.credits, sub.currency, l)],
          [t('billing.startedAt'), dateTime(sub.started_at, l)],
        ]}
      />
      <div className="mt-4">
        <div className="flex items-baseline justify-between text-[12.5px]">
          <span className="text-muted">{t('billing.pool')}</span>
          <span className="font-mono text-ink" data-testid="pool">
            {money(sub.pool_spent, sub.currency, l)} / {pool === null ? t('orgs.unlimited') : money(pool, sub.currency, l)}
          </span>
        </div>
        {pool !== null && (
          <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-hover" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(ratio * 100)}>
            <div className={`h-full rounded-full ${ratio >= 1 ? 'bg-danger' : ratio > 0.8 ? 'bg-warn' : 'bg-accent'}`} style={{ width: `${ratio * 100}%` }} />
          </div>
        )}
        {sub.pool_prorated_since && sub.pool_full !== null && (
          <p className="mt-1.5 text-[12px] text-muted" data-testid="prorated">
            {t('billing.prorated', { date: dateOnly(sub.pool_prorated_since, l), full: money(sub.pool_full, sub.currency, l), next: dateOnly(sub.current_period_end, l) })}
          </p>
        )}
      </div>
      {sub.scheduled && (
        <div className="mt-3">
          <Alert tone="warn">
            {t(sub.scheduled.credit_tier_price === null ? 'billing.scheduled' : 'billing.scheduledTier', {
              offer: `${sub.scheduled.offer_name} v${sub.scheduled.version}`,
              seats: sub.scheduled.seats,
              tier: money(sub.scheduled.credit_tier_price, sub.currency, l),
              date: dateOnly(sub.scheduled.effective_at, l),
            })}
          </Alert>
        </div>
      )}
      {sub.cancel_at_period_end && (
        <div className="mt-3">
          <Alert tone="warn">{t('billing.cancelsAt', { date: dateOnly(sub.current_period_end, l) })}</Alert>
        </div>
      )}
    </Card>
  );
}

function versionPrice(v: OfferVersion, t: (k: string, o?: Record<string, unknown>) => string, locale: string): string {
  if (v.trial_days) return t('billing.trialDays', { days: v.trial_days });
  const seat = `${money(v.price_per_seat, v.currency, locale)} ${t('billing.perSeat')}`;
  if (!v.credit_tiers?.length) return seat;
  return Number(v.price_per_seat) > 0 ? `${t('billing.withTiers')} + ${seat}` : t('billing.withTiers');
}

/** Every version of every offer, newest first within its offer: staff may put anyone on any of them. An offer's key
 * repeats across currencies, so each is named with its currency. */
function versionOptions(offers: OfferAdmin[], t: (k: string, o?: Record<string, unknown>) => string, locale: string) {
  return offers.flatMap((o) =>
    (o.versions.length ? o.versions : [o.terms]).map((v) => ({
      id: v.id,
      offer: o,
      label: `${o.name} (${keyIn(o.key, v.currency)}) v${v.version} — ${versionPrice(v, t, locale)}${o.public ? '' : ` · ${t('offers.private')}`}${v.id === o.terms.id ? '' : ` · ${t('offers.older')}`}`,
      version: v,
    })),
  );
}

function SetSubscription({ org, sub, busy, run }: { org: OrganizationDetail; sub: Subscription | null; busy: string | null; run: Run }) {
  const { t, i18n } = useTranslation();
  const offers = useAsync(() => api.offers(), []);
  const options = versionOptions(offers.data ?? [], t, i18n.language);
  const [versionId, setVersionId] = useState<number | ''>('');
  const [seats, setSeats] = useState(String(sub?.seats ?? 1));
  const [tier, setTier] = useState('');
  const [confirm, setConfirm] = useState(false);
  const chosen = options.find((o) => o.id === versionId);
  const tiers = chosen?.version.credit_tiers ?? [];
  const sameVersion = sub !== null && versionId === sub.offer_version_id;
  // A version in another currency moves the organization to it, which the API refuses once it paid (`currency_locked`).
  const switches = chosen !== undefined && chosen.version.currency !== org.currency;
  const choose = (id: number | '') => {
    setVersionId(id);
    const v = options.find((o) => o.id === id)?.version;
    const keep = v?.credit_tiers?.find((x) => sub?.credit_tier_price !== null && Number(x.price) === Number(sub?.credit_tier_price));
    setTier(keep ? String(keep.price) : v?.credit_tiers?.[0] ? String(v.credit_tiers[0].price) : '');
  };
  const offerLabel = chosen ? (tiers.length ? `${chosen.label} · ${t('billing.tierPart', { price: money(tier, chosen.version.currency, i18n.language), usage: money(tiers.find((x) => Number(x.price) === Number(tier))?.usage, chosen.version.currency, i18n.language) })}` : chosen.label) : '';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setConfirm(true);
  };
  return (
    <Card title={t('billing.setTitle')}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <p className="text-[12px] text-muted">{t('billing.setHint')}</p>
        <div className="grid grid-cols-[1fr_96px] gap-3">
          <Field label={t('billing.offerVersion')}>
            <Select required value={versionId} onChange={(e) => choose(e.target.value ? Number(e.target.value) : '')} data-testid="set-version">
              <option value="">{t('billing.chooseVersion')}</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('billing.seats')}>
            <Input type="number" min={1} required value={seats} onChange={(e) => setSeats(e.target.value)} data-testid="set-seats" />
          </Field>
        </div>
        {switches && chosen && <Alert tone="warn">{t('billing.switchesCurrency', { from: org.currency, to: chosen.version.currency })}</Alert>}
        {tiers.length > 0 && (
          <Field label={t('billing.tier')}>
            <Select required value={tier} onChange={(e) => setTier(e.target.value)} data-testid="set-tier">
              {tiers.map((x) => (
                <option key={String(x.price)} value={String(x.price)}>
                  {t('billing.tierPart', { price: money(x.price, chosen?.version.currency, i18n.language), usage: money(x.usage, chosen?.version.currency, i18n.language) })}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <div className="flex justify-end">
          <Button type="submit" variant="primary" disabled={!chosen}>
            {t('billing.applyNow')}
          </Button>
        </div>
      </form>
      <Confirm
        open={confirm}
        title={t('billing.setTitle')}
        message={chosen ? t(sameVersion ? 'billing.setSeatsConfirm' : 'billing.setConfirm', { name: org.name, offer: offerLabel, seats }) : ''}
        confirmLabel={t('billing.applyNow')}
        busy={busy === 'set-sub'}
        onCancel={() => setConfirm(false)}
        onConfirm={() => chosen && void run('set-sub', () => api.setSubscription(org.id, chosen.id, parseInt(seats, 10) || 1, tiers.length ? tier : null), t('billing.set')).then(() => setConfirm(false))}
      />
    </Card>
  );
}

function Extend({ org, disabled, busy, run }: { org: OrganizationDetail; disabled: boolean; busy: string | null; run: Run }) {
  const { t } = useTranslation();
  const [days, setDays] = useState('7');
  return (
    <Card title={t('billing.extendTitle')}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void run('extend', () => api.extendSubscription(org.id, parseInt(days, 10) || 1), t('billing.extended', { days }));
        }}
        className="flex flex-col gap-3"
      >
        <Field label={t('billing.days')} hint={t('billing.extendHint')}>
          <Input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} />
        </Field>
        <Button type="submit" disabled={disabled} busy={busy === 'extend'}>
          {t('billing.extend')}
        </Button>
      </form>
    </Card>
  );
}

function Grant({ org, busy, run }: { org: OrganizationDetail; busy: string | null; run: Run }) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [expires, setExpires] = useState('');
  const [note, setNote] = useState('');
  return (
    <Card title={t('billing.grantTitle')}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const value = toDecimal(amount);
          if (!value) return;
          void run('grant', () => api.grantCredits(org.id, value, expires ? new Date(`${expires}T23:59:59`).toISOString() : null, note.trim()), t('billing.granted')).then(() => {
            setAmount('');
            setNote('');
          });
        }}
        className="flex flex-col gap-3"
      >
        <div className="grid grid-cols-2 gap-2">
          <Field label={t('billing.amountIn', { currency: org.currency })}>
            <Input required type="number" min={0.01} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="grant-amount" />
          </Field>
          <Field label={t('billing.expires')}>
            <Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
          </Field>
        </div>
        <Field label={t('billing.note')}>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('billing.notePlaceholder')} />
        </Field>
        <Button type="submit" busy={busy === 'grant'}>
          {t('billing.grant')}
        </Button>
      </form>
    </Card>
  );
}

function Credits({ org }: { org: OrganizationDetail }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const now = Date.now();
  return (
    <Card title={t('billing.creditsTitle')}>
      <Table head={[t('billing.source'), t('billing.amountIn', { currency: org.currency }), t('billing.remaining'), t('billing.expires'), t('billing.note'), t('orgs.createdAt')]} empty={t('billing.noCredits')}>
        {org.credits.map((g) => {
          const expired = g.expires_at !== null && new Date(g.expires_at).getTime() <= now;
          return (
            <Row key={g.id} className={expired ? 'opacity-50' : ''}>
              <Cell>{t(`billing.grantSource.${g.source}`)}</Cell>
              <Cell mono>{money(g.amount_eur, g.currency, l)}</Cell>
              <Cell mono>{money(g.remaining_eur, g.currency, l)}</Cell>
              <Cell mono>
                {dateOnly(g.expires_at, l)}
                {expired && (
                  <>
                    {' '}
                    <Badge tone="bad">{t('billing.expired')}</Badge>
                  </>
                )}
              </Cell>
              <Cell>{g.note || '—'}</Cell>
              <Cell mono>{dateTime(g.created_at, l)}</Cell>
            </Row>
          );
        })}
      </Table>
    </Card>
  );
}

export function OrderActions({ order, busy, run, onInvoiced }: { order: { id: number; status: string; invoice_id: number | null }; busy: string | null; run: Run; onInvoiced?: (invoiceId: number) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [paying, setPaying] = useState(false);
  return (
    <div className="flex justify-end gap-1">
      {order.status === 'pending' && (
        <>
          <Button variant="primary" busy={busy === `paid-${order.id}`} onClick={() => setPaying(true)} data-testid={`paid-${order.id}`}>
            {t('orders.markPaid')}
          </Button>
          <Button variant="danger" busy={busy === `cancel-${order.id}`} onClick={() => void run(`cancel-${order.id}`, () => api.cancelOrder(order.id), t('orders.canceled'))}>
            {t('common.cancel')}
          </Button>
        </>
      )}
      {order.invoice_id !== null ? (
        <Button variant="ghost" onClick={() => navigate(`/invoices/${order.invoice_id}`)}>
          {t('orders.openInvoice')}
        </Button>
      ) : (
        order.status !== 'canceled' && (
          <Button
            variant="ghost"
            busy={busy === `invoice-${order.id}`}
            onClick={() =>
              void run(`invoice-${order.id}`, async () => {
                const invoice = await api.invoiceOrder(order.id);
                onInvoiced?.(invoice.id);
              })
            }
          >
            {t('orders.draftInvoice')}
          </Button>
        )
      )}
      <Confirm
        open={paying}
        title={t('orders.markPaidTitle', { id: order.id })}
        message={t('orders.markPaidMessage')}
        confirmLabel={t('orders.markPaid')}
        busy={busy === `paid-${order.id}`}
        onCancel={() => setPaying(false)}
        onConfirm={() => void run(`paid-${order.id}`, () => api.markOrderPaid(order.id), t('orders.paid')).then(() => setPaying(false))}
      />
    </div>
  );
}

function Orders({ org, busy, run }: { org: OrganizationDetail; busy: string | null; run: Run }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const l = i18n.language;
  const [payments, setPayments] = useState<number | null>(null);
  const [refunds, setRefunds] = useState<number | null>(null);
  const refunding = org.orders.find((o) => o.id === refunds);
  return (
    <>
    <Card title={t('orders.title')}>
      <Table head={['#', t('orders.kindLabel'), t('orders.statusLabel'), t('orders.what'), t('orders.amount'), t('orders.vat'), t('orders.total'), t('orders.payment'), t('orders.createdAt'), t('orders.paidAt'), '']} empty={t('orders.none')}>
        {org.orders.map((o) => (
          <Row key={o.id}>
            <Cell mono>{o.id}</Cell>
            <Cell>{t(`orders.kind.${o.kind}`)}</Cell>
            <Cell>
              <Badge tone={statusTone(o.status)}>{t(`orders.status.${o.status}`)}</Badge>
            </Cell>
            <Cell className="max-w-[280px] truncate" title={o.lines.map((x) => x.description).join('; ')}>
              {o.lines.map((x) => x.description).join('; ') || '—'}
            </Cell>
            <OrderAmounts order={o} />
            <Cell><OrderPaymentBadge order={o} /></Cell>
            <Cell mono>{dateTime(o.created_at, l)}</Cell>
            <Cell mono>{dateTime(o.paid_at, l)}</Cell>
            <Cell>
              <div className="flex flex-wrap gap-1.5">
                <OrderActions order={o} busy={busy} run={run} onInvoiced={(id) => navigate(`/invoices/${id}`)} />
                <PaymentsToggle order={o} open={payments === o.id} onToggle={setPayments} />
                <RefundsToggle order={o} open={refunds === o.id} onToggle={setRefunds} />
              </div>
            </Cell>
          </Row>
        ))}
      </Table>
    </Card>
    {payments !== null && <OrderPayments orderId={payments} onClose={() => setPayments(null)} />}
    {refunding && <OrderRefunds order={refunding} onClose={() => setRefunds(null)} onRefunded={() => void run('refunded', async () => undefined)} />}
    </>
  );
}
